import { createSession, type EditorSession } from '@/application';
import { bounds as boundsOps, type Bounds } from '@/domain/geometry';
import { type Vec2 } from '@/domain/math';
import { type EntityId, inDrawOrder, type SceneDocument } from '@/domain/model';
import {
  Autosave,
  type AutosaveState,
  browserDownloadHost,
  exportDocument,
  importDocument,
  KeyValueDocumentRepository,
  importImage,
  localStorageStore,
  memoryStore,
  placementSize,
} from '@/infrastructure';
import * as accounts from '@/infrastructure/accountService';
import { type FigureResult, requestFigure } from '@/infrastructure/figureService';
import { MAX_DRAWING_BYTES, sendReport } from '@/infrastructure/reportService';
import { ToolController } from '@/input';
import {
  buildDrawing,
  DEFAULT_PAGE_SETUP,
  type DrawingFormat,
  type PageSetup,
  type Renderer,
  withFrameOfReference,
} from '@/rendering';
import { encodeToJson } from '@/serialization';
import { createEditorStore, type EditorStore } from '@/store';
import { type AccountApi } from '@/ui/accountPort';
import { type ReportApi } from '@/ui/reportPort';

import { createDemoDocument } from './demoDocument';

/**
 * The paper choices the file bar offers, as page setups.
 *
 * Named by outcome rather than by parameter — "A4, tiled" instead of "fitToPage: false" —
 * because the user is choosing what comes out of the printer, not configuring a layout
 * engine.
 */
export type PrintPaper = 'a4' | 'a4-landscape' | 'letter' | 'a4-tiled';

const PRINT_SETUPS: Readonly<Record<PrintPaper, PageSetup>> = {
  a4: { ...DEFAULT_PAGE_SETUP, paper: 'a4', fitToPage: true },
  'a4-landscape': {
    ...DEFAULT_PAGE_SETUP,
    paper: 'a4',
    orientation: 'landscape',
    fitToPage: true,
  },
  letter: { ...DEFAULT_PAGE_SETUP, paper: 'letter', fitToPage: true },
  // Full size across as many sheets as it takes, for a scale drawing to be taped together.
  'a4-tiled': { ...DEFAULT_PAGE_SETUP, paper: 'a4', fitToPage: false },
};

/**
 * The composition root.
 *
 * The only module that knows about every layer at once. It builds the document, wraps it
 * in a session, constructs the tool controller, wires persistence, and hands the UI plain
 * values and callbacks.
 *
 * The UI therefore never imports `engine`, `serialization`, `infrastructure` or
 * `bootstrap` — which is exactly what `import-x/no-restricted-paths` enforces.
 */

const DOCUMENT_ID = 'current';
const STORAGE_KEY_PREFIX = 'edusketch';

export interface AppWiring {
  readonly controller: ToolController;
  /** The view-model the panels read from. Created here so the UI never builds one. */
  readonly store: EditorStore;
  readonly tools: readonly { id: string; label: string; shortcut?: string }[];
  readonly onRendererReady: (renderer: Renderer) => void;
  readonly initialFit: Bounds;
  readonly file: FileWiring;
  /**
   * Turns a problem statement into a script, by way of the model proxy.
   *
   * Wired here because this is the only layer allowed to reach `infrastructure`, and it is a
   * plain function rather than an object because there is one thing to do. The panel treats its
   * absence as a supported state, so an editor with no proxy behind it still works — which is
   * what running the tests, and running it offline, both need.
   */
  readonly drawFromProblem: (problem: string) => Promise<FigureResult>;
  /**
   * The account port, or `undefined` when this build has no API to talk to.
   *
   * Wired here because `src/ui` may not reach `src/infrastructure` (ADR-0001) — the shell is
   * handed an implementation and never learns that an account is a `fetch` across an origin.
   */
  readonly accountApi: AccountApi | undefined;
  /** Cổng gửi báo lỗi. Cùng lý do phải nối ở đây như `accountApi`. */
  readonly reportApi: ReportApi | undefined;
}

export interface FileWiring {
  readonly subscribeSaveState: (listener: (state: AutosaveState) => void) => () => void;
  readonly currentSaveState: () => AutosaveState;
  readonly warnings: readonly string[];
  exportFile(): void;
  /**
   * Saves the drawing as a picture: PNG, SVG, PDF or JPEG.
   *
   * With `only`, saves just those entities, cropped to them — which is how the variation
   * table and the curve it describes become two files rather than one picture nobody can cut
   * apart afterwards.
   */
  /**
   * Writes the drawing out as a picture.
   *
   * `aspect` is the width ÷ height the file is padded to. Omitted keeps the shape of the drawing
   * itself, which is the right default — a figure a user has not asked to be square should not
   * arrive square, with `y = sin x` a squiggle across the middle of a white box.
   */
  exportDrawing(
    format: DrawingFormat,
    only?: ReadonlySet<EntityId>,
    aspect?: number
  ): Promise<void>;
  /** Saves a PDF sized to paper rather than to the drawing. */
  printDrawing(paper: PrintPaper): Promise<void>;
  importFile(file: File): Promise<readonly string[]>;
  /** Places a picture on the canvas. Resolves to a reason when it could not be used. */
  importImage(file: File, at: Vec2): Promise<string | null>;
  saveNow(): Promise<void>;
  /**
   * The current document as JSON text, for the crash-recovery download.
   *
   * Separate from `exportFile` because it must not touch the DOM: it runs from an error
   * boundary, after something has already gone wrong, and the boundary decides for itself
   * what to do with the text.
   */
  snapshot(): string;
}

/**
 * Chooses a storage backend.
 *
 * `localStorage` throws in a sandboxed iframe and in some privacy modes, so the probe is
 * a real write rather than a feature check. Falling back to memory keeps the editor
 * usable — losing autosave is much better than refusing to start.
 */
function createStore() {
  try {
    const probe = `${STORAGE_KEY_PREFIX}:probe`;
    globalThis.localStorage.setItem(probe, '1');
    globalThis.localStorage.removeItem(probe);
    return localStorageStore(globalThis.localStorage);
  } catch {
    return memoryStore();
  }
}

export function composeApp(): AppWiring {
  const repository = new KeyValueDocumentRepository(createStore());

  const startingDocument: SceneDocument = createDemoDocument();
  const session = createSession(startingDocument);
  const controller = new ToolController(session);
  const { store: editorStore } = createEditorStore(controller);

  const autosave = new Autosave(repository, DOCUMENT_ID);
  let warnings: readonly string[] = [];
  let preserved: Readonly<Record<string, unknown>> = {};

  /*
   * Autosave stays disarmed until the stored document has been restored.
   *
   * Storage is async, so the editor necessarily starts on the demo document. Without
   * this gate, the user's first edit would fire an autosave of *the demo* and overwrite
   * whatever they had saved before the restore landed.
   */
  let restored = false;

  /*
   * The document, and only when it is a different one.
   *
   * `subscribe` fires for everything the shell needs to repaint — the cursor, the tool prompt,
   * a point lit up under a panel's pointer — and none of those is an edit. Scheduling a save on
   * each of them marked the file dirty for a mouse moving across a row of buttons, and worse: a
   * save schedules a status change, which re-renders React, which can emit again. That is a loop
   * with a document that never changed anywhere in it.
   *
   * A reference comparison is exact here rather than approximate: the document is immutable and
   * every edit builds a new one.
   */
  let lastSaved: SceneDocument | null = null;

  controller.subscribe((view) => {
    if (!restored) return;
    if (view.editor.document === lastSaved) return;

    lastSaved = view.editor.document;
    autosave.schedule(view.editor.document);
  });

  void repository
    .load(DOCUMENT_ID)
    .then((stored) => {
      if (stored !== null) {
        preserved = stored.unknown;
        warnings = stored.warnings;
        autosave.adopt(stored.summary.revision, stored.unknown);
        controller.replaceSession(createSession(stored.document));
      }
    })
    .catch((error: unknown) => {
      // A corrupt stored document must not take the editor down with it. The demo stays
      // on screen and the user is told why their work did not come back.
      warnings = [
        `could not restore your last document: ${error instanceof Error ? error.message : String(error)}`,
      ];
    })
    .finally(() => {
      restored = true;
    });

  // A last-chance flush. `visibilitychange` rather than `beforeunload`: the latter is
  // unreliable on mobile, where a backgrounded tab may simply be discarded.
  if (typeof globalThis.addEventListener === 'function') {
    globalThis.addEventListener('visibilitychange', () => {
      if (globalThis.document.visibilityState === 'hidden') void autosave.flush();
    });
  }

  /*
   * The signal is passed through, and that is the whole of the change.
   *
   * `requestFigure` has always taken one and this line has always dropped it, so a request that
   * had already run for three minutes — which a hard problem does, because the endpoint takes a
   * second corrective turn — could not be called off. The panel now owns an `AbortController`
   * and hands it down; there is nothing for this layer to decide.
   */
  /*
   * The account port, assembled from the service.
   *
   * Named methods rather than the module itself, so the shape the interface promises is the shape
   * that is actually satisfied — a module passed wholesale would carry whatever else it exports
   * and would keep type-checking when the port and the service drifted apart.
   */
  const accountApi: AccountApi = {
    readMe: accounts.readMe,
    requestCode: accounts.requestCode,
    verifyCode: accounts.verifyCode,
    setMarketingConsent: accounts.setMarketingConsent,
    signOut: accounts.signOut,
    signOutEverywhere: accounts.signOutEverywhere,
    deleteAccount: accounts.deleteAccount,
    exportMyData: accounts.exportMyData,
  };

  const reportApi: ReportApi = {
    send: sendReport,
    maxDrawingBytes: MAX_DRAWING_BYTES,
  };

  const drawFromProblem = (problem: string, signal?: AbortSignal): Promise<FigureResult> =>
    requestFigure(problem, signal);

  const file: FileWiring = {
    subscribeSaveState: (listener) => autosave.subscribe(listener),
    currentSaveState: () => autosave.current,
    get warnings() {
      return warnings;
    },
    exportDrawing: async (format, only, aspect) => {
      /*
       * The seam the boundary rule forced, and it turned out to be the right one:
       * `rendering` turns the scene into bytes, and only this line knows those bytes become
       * a download. The first version put both halves in `infrastructure` and the lint
       * rejected it.
       */
      const scene = controller.view.editor.scene;
      // A graph brings its axes: see `withFrameOfReference` for why a curve without them is
      // not a smaller picture of the same thing but a different, useless one.
      const subset = only === undefined ? undefined : withFrameOfReference(scene, only);

      const blob = await buildDrawing(scene, {
        format,
        ...(subset === undefined ? {} : { only: subset }),
        // PDF states its proportions through `page`; a second shape on top of that would leave
        // the figure floating inside margins nobody asked for.
        ...(aspect === undefined || format === 'pdf' ? {} : { aspect }),
        // Transparent for SVG, where a worksheet usually wants the page to show through;
        // white for the rest, because a transparent PNG on a dark slide is black on black.
        background: format === 'svg' ? null : '#ffffff',
      });

      const host = browserDownloadHost(globalThis.document);
      const url = host.createObjectURL(blob);
      try {
        // Named for what it holds, so two exports in a row do not overwrite each other in
        // the downloads folder — which is exactly the case this feature exists for.
        host.triggerDownload(
          url,
          `edusketch-${subset === undefined ? 'drawing' : 'phan-chon'}.${format}`
        );
      } finally {
        // A leaked object URL pins the blob for the life of the page, and an export is the
        // largest blob this application makes.
        host.revokeObjectURL(url);
      }
    },
    printDrawing: async (paper) => {
      const setup = PRINT_SETUPS[paper];
      const blob = await buildDrawing(controller.view.editor.scene, {
        format: 'pdf',
        background: '#ffffff',
        page: setup,
      });

      const host = browserDownloadHost(globalThis.document);
      const url = host.createObjectURL(blob);
      try {
        host.triggerDownload(url, `edusketch-${paper}.pdf`);
      } finally {
        host.revokeObjectURL(url);
      }
    },
    exportFile: () => {
      exportDocument(browserDownloadHost(globalThis.document), controller.view.editor.document, {
        preserve: preserved,
      });
    },
    snapshot: () => encodeToJson(controller.view.editor.document, { preserve: preserved }),
    /*
     * The picture is read here, in the layer that owns the browser, and handed to the
     * controller as bytes and a size. Reading a file is asynchronous and has nothing to do
     * with what a document becomes — keeping the seam there is what lets image placement be
     * tested with no DOM at all.
     */
    importImage: async (fileHandle: File, at: Vec2) => {
      try {
        const image = await importImage(fileHandle);
        const size = placementSize(image);
        controller.placeImage({ href: image.href, ...size }, at);
        return null;
      } catch (error: unknown) {
        // Returned rather than thrown: a rejected picture is an ordinary outcome with a
        // reason the user can act on, not a failure of the application.
        return error instanceof Error ? error.message : String(error);
      }
    },
    importFile: async (fileHandle: File) => {
      const loaded = await importDocument(fileHandle);
      preserved = loaded.unknown;
      warnings = loaded.warnings;
      autosave.adopt(null, loaded.unknown);

      const next: EditorSession = createSession(loaded.document);
      controller.replaceSession(next);
      return loaded.warnings;
    },
    saveNow: async () => {
      autosave.schedule(controller.view.editor.document);
      await autosave.flush();
    },
  };

  let box = boundsOps.EMPTY;
  for (const entity of inDrawOrder(session.state.document)) {
    const value = session.state.scene.values.get(entity.id);
    if (value?.kind === 'point') box = boundsOps.union(box, boundsOps.fromPoint(value.point));
  }
  const initialFit = boundsOps.isEmpty(box)
    ? boundsOps.bounds(-200, -200, 200, 200)
    : boundsOps.expand(box, 60);

  return {
    controller,
    store: editorStore,
    tools: controller.availableTools,
    onRendererReady: (renderer) => {
      controller.setZoom(renderer.camera.zoom);
    },
    initialFit,
    file,
    drawFromProblem,
    accountApi,
    reportApi,
  };
}
